// web/src/contexts/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

/* ============================================================
   Single-flight refresh + helpers
   ============================================================ */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
let inflightRefresh = null;
let lastRefreshAt = 0;

// Cross-tab ping that a refresh succeeded
const LS_REFRESH_KEY = "__edl_auth_refreshed__";
function broadcastRefreshed() {
  try {
    localStorage.setItem(LS_REFRESH_KEY, String(Date.now()));
    localStorage.removeItem(LS_REFRESH_KEY);
  } catch {
    // ignore
  }
}

// Do a refresh exactly once concurrently; retry once if "already used"
async function ensureFreshSession() {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    try {
      let r = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });

      if (!r.ok) {
        // If another tab already rotated the token, retry once after a short delay
        let detail = "";
        try {
          const j = await r.json();
          detail = String(j?.detail || "");
        } catch {
          // ignore parse errors
        }
        if (detail.toLowerCase().includes("already used")) {
          await sleep(250);
          r = await fetch("/api/auth/refresh", {
            method: "POST",
            credentials: "include",
          });
        }
      }

      if (!r.ok) return false;

      lastRefreshAt = Date.now();
      broadcastRefreshed();
      return true;
    } catch {
      return false;
    }
  })().finally(() => {
    inflightRefresh = null;
  });

  return inflightRefresh;
}

/* ============================================================
   Optional: API wrapper to auto-retry once after a refresh
   Use it anywhere you do authenticated fetches.
   ============================================================ */
export async function apiFetch(input, init = {}) {
  const first = await fetch(input, { credentials: "include", ...init });

  if (first.status !== 401) {
    return first;
  }

  const ok = await ensureFreshSession();
  if (!ok) return first; // still 401 → propagate

  return fetch(input, { credentials: "include", ...init });
}

/* ============================================================
   Auth Context
   ============================================================ */
const defaultAuth = {
  user: null,
  loading: true,
  login: async () => false,
  signup: async () => false,
  refresh: async () => false,
  logout: async () => {},
  openAuthModal: (_mode = "login") => {},
  closeAuthModal: () => {},
};

const AuthCtx = createContext(defaultAuth);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch /me; on 401, attempt refresh → retry /me
  const fetchMe = useCallback(async () => {
    try {
      let r = await fetch("/api/auth/me", {
        credentials: "include",
        cache: "no-store",
      });

      if (r.status === 401) {
        const ok = await ensureFreshSession();
        if (!ok) {
          setUser(null);
          return null;
        }
        r = await fetch("/api/auth/me", {
          credentials: "include",
          cache: "no-store",
        });
      }

      if (!r.ok) throw new Error(`HTTP ${r.status}`);

      const me = await r.json();
      setUser(me);
      return me;
    } catch (e) {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      const r = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!r.ok) {
        let msg = "Échec de connexion";
        try {
          const j = await r.json();
          msg = j?.detail || msg;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      await fetchMe();
      lastRefreshAt = Date.now();
      broadcastRefreshed();
      window.dispatchEvent(new Event("auth:changed"));
      return true;
    },
    [fetchMe]
  );

  const signup = useCallback(
    async (email, name, password) => {
      const r = await fetch("/api/auth/signup", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, password }),
      });

      if (!r.ok) {
        let msg = "Échec d’inscription";
        try {
          const j = await r.json();
          msg = j?.detail || msg;
        } catch {
          // ignore
        }
        throw new Error(msg);
      }

      await fetchMe();
      lastRefreshAt = Date.now();
      broadcastRefreshed();
      window.dispatchEvent(new Event("auth:changed"));
      return true;
    },
    [fetchMe]
  );

  const refresh = useCallback(async () => {
    const ok = await ensureFreshSession();
    if (ok) await fetchMe();
    return ok;
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }
    setUser(null);
    window.dispatchEvent(new Event("auth:changed"));
  }, []);

  // Global auth modal helpers
  const openAuthModal = useCallback((mode = "login") => {
    try {
      window.dispatchEvent(
        new CustomEvent("open-auth-modal", { detail: { mode } })
      );
    } catch {
      // ignore
    }
  }, []);

  const closeAuthModal = useCallback(() => {
    try {
      window.dispatchEvent(new Event("close-auth-modal"));
    } catch {
      // ignore
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // 🔄 Refresh when tab becomes visible / online (counts as user activity)
  useEffect(() => {
    const onVisOrOnline = async () => {
      if (!document.hidden) {
        // Only bother if it's been a bit since last refresh
        if (Date.now() - lastRefreshAt >= 4 * 60 * 1000) {
          const ok = await ensureFreshSession();
          if (ok) await fetchMe();
        }
      }
    };

    document.addEventListener("visibilitychange", onVisOrOnline);
    window.addEventListener("online", onVisOrOnline);

    return () => {
      document.removeEventListener("visibilitychange", onVisOrOnline);
      window.removeEventListener("online", onVisOrOnline);
    };
  }, [fetchMe]);

  // Cross-tab: if another tab refreshed, just refetch /me here
  useEffect(() => {
    const onStorage = async (e) => {
      if (e.key === LS_REFRESH_KEY) {
        await fetchMe();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [fetchMe]);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      signup,
      refresh,
      logout,
      openAuthModal,
      closeAuthModal,
    }),
    [user, loading, login, signup, refresh, logout, openAuthModal, closeAuthModal]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
